import * as Yup from 'yup'
import {useState, forwardRef, useEffect} from 'react'
import {useNavigate, Link} from 'react-router-dom'
import {useFormik, Form, FormikProvider} from 'formik'
import {useSnackbar} from 'notistack5'
import SvgIcon from '@material-ui/core/SvgIcon'
import useNetworkMonitor from 'utils/hooks/useNetworkMonitor'
import {Icon} from '@iconify/react'
import eyeFill from '@iconify/icons-eva/eye-fill'
import eyeOffFill from '@iconify/icons-eva/eye-off-fill'
import userLight from '@iconify/icons-ph/user-light'
// material
import {
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Modal,
  Backdrop,
  Fade,
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  Slide,
  DialogContent,
  DialogActions,
  DialogTitle,
  DialogContentText,
  CircularProgress,
} from '@material-ui/core'
import CloseIcon from '@mui/icons-material/Close'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import {LoadingButton} from '@material-ui/lab'
import {Loading} from 'components/timeButton'
import {HighlightOff as HighlightOffIcon} from '@mui/icons-material'
import SwitchedIcon from 'assets/svg/loginSwitched.png'
import EmployeeVisualAid from 'assets/svg/stk-employee-reg-visual-aid.png'
import storage from 'utils/storage'
import userApi from 'utils/api/users'
import useAuth from 'utils/api/auth'
import Bugsnag from '@bugsnag/js'
import QrReader from 'react-qr-reader'
import ChangePass from 'components/account/ChangePass'
// ----------------------------------------------------------------------

export default function LoginForm({handleSwitchForm}) {
  const navigate = useNavigate()
  const {enqueueSnackbar} = useSnackbar()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setLoading] = useState(false)

  // Use shared network monitoring
  useNetworkMonitor()
  const [openCreateAccount, setOpenCreateAccount] = useState(false)
  const [openPasswordReset, setOpenPasswordReset] = useState(false)
  const [openQR, setOpenQR] = useState(false)
  const [scanMessage, setScanMessage] = useState('No data found')
  const [initialChoice, setInitialChoice] = useState(false) // Tracks initial choice modal visibility
  const [isStoreManager, setIsStoreManager] = useState(false) // Tracks if user chose "Store Manager"

  // Create formik instance
  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  })

  // Initialize with empty values
  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: storage.getRememberMe() ? true : false,
    },
    validationSchema: LoginSchema,
    onSubmit: async () => {
      setLoading(true)

      try {
        if (!values.email || !values.password) {
          setLoading(false)
          return
        }

        // Add timeout to the API call
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Login request timed out')), 30000),
        )
        const uiVersion = localStorage.getItem('uiVersion') || 'old'
        const result = await Promise.race([
          useAuth.sign_in_email(values.email, values.password, uiVersion),
          timeoutPromise,
        ])

        if (!result.ok) {
          if (result.data.msg === 'User is archived!') {
            try {
              const loginResult = await useAuth.archived_user_login_email(values.email, values.password)
              if (loginResult && loginResult.data && loginResult.data.success) {
                // Store token and user data for archived access
                await storage.storeToken(loginResult.data.token)
                await storage.storeUser(loginResult.data.user)
    
                // Redirect to archived gate page with authentication
                navigate('/archived', {
                  state: {
                    userId: loginResult.data.user._id,
                    isArchived: true,
                    role: loginResult.data.user.role || 0,
                    company: loginResult.data.user.company || 'Unknown Company'
                  }
                }, {replace: true})
                return
              }else{
                enqueueSnackbar(loginResult.data.msg, {variant: 'error'})
                setLoading(false)
                return
              }
            } catch (loginError) {
              console.error('Archived user login error:', loginError)
            }
        } else {
          Bugsnag.notify(result)
          enqueueSnackbar('Invalid username or password', {variant: 'error'})
          setLoading(false)
          return
        }
      }

        let {data} = result
        if (data.role === 0) {
          data.sid = data.store_id
        }

        if (!JSON.parse(data.isOnBoarded)) {
          setLoading(false)
          navigate(`/${data.sid}/onboard`)
          return
        }

        resetForm()

        const isWebView =
          navigator.userAgent.includes('wv') || // Android WebView
          (navigator.userAgent.includes('Mobile/') && !navigator.userAgent.includes('Safari')) // iOS WebView

        if (data.role === 1 || data.role >= 3) {
          if (isWebView) {
            enqueueSnackbar(
              'Important Notice: Store manager accounts require browser access for all features. Please open sparkletimekeeping.com in Chrome, Safari, or another web browser.',
              {variant: 'warning'},
            )
            setLoading(false)
            navigate(`/login`, {replace: true})
            return
          }
        }

        // Store user data and token regardless of remember me
        await storage.storeUser(data)
        await storage.storeToken(data.token)

        // Store or clear credentials based on remember me selection
        if (values.remember) {
          storage.storeRememberMe({email: values.email, password: values.password})
        } else {
          storage.removeRememberMe()
        }

        setLoading(false)

        if (data.role === 1 || data.role >= 3) {
          navigate(`/stores/admin`, {replace: true})
          return
        }

        navigate(`/dashboard/app`, {replace: true})
      } catch (error) {
        console.error('Login error:', error)
        setLoading(false)

        if (error.message === 'Login request timed out') {
          enqueueSnackbar('Login request timed out. Please check your internet connection and try again.', {
            variant: 'error',
          })
        } else {
          enqueueSnackbar('An error occurred during login. Please try again.', {variant: 'error'})
        }
      }
    },
  })

  const {errors, touched, values, resetForm, handleSubmit, getFieldProps, setFieldValue} = formik

  // Load remembered credentials on component mount - AFTER formik is initialized
  useEffect(() => {
    try {
      const rememberedData = storage.getRememberMe()

      if (rememberedData && rememberedData.email && rememberedData.password) {
        setFieldValue('email', rememberedData.email)
        setFieldValue('password', rememberedData.password)
        setFieldValue('remember', true)
      }
    } catch (error) {
      console.log('Error loading remembered credentials', error)
    }
  }, [setFieldValue])

  const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />
  })

  const handleUserChoice = (choice) => {
    if (choice === 'Employee') {
      setIsStoreManager(false)
      setOpenCreateAccount(true)
    } else if (choice === 'Client Manager') {
      setIsStoreManager(true)
      setOpenCreateAccount(true)
    }
    setInitialChoice(false)
  }

  const handleShowPassword = () => {
    setShowPassword((show) => !show)
  }

  const handleClosePassword = () => {
    setOpenPasswordReset(false)
  }

  /*  useEffect(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (connection) {
      const { type, effectiveType, downlink } = connection;
      if(downlink < 0.5) {
        alert("Slow internet connection please wait")
      }
    }
  }, []);
  useEffect(() => {

    const interval = setInterval(() => {

      if (!navigator.onLine) {
        alert('You are offline. Please check your internet connection.');
      }

    }, 1000);

    return () => clearInterval(interval);
  }, [])*/

  const handleScan = async (res) => {
    if (res) {
      window.location.href = res
    } else {
      enqueueSnackbar('No link found in the response.', {variant: 'warning'})
    }
  }

  const handleCloseQR = async () => {
    setOpenQR(false)
  }

  const handleOpenCreateAccount = async () => {
    setInitialChoice(true)
    // setOpenCreateAccount(true)
  }

  const handleCloseCreateAccount = async () => {
    setOpenCreateAccount(false)
  }

  const handleError = (err) => {
    enqueueSnackbar(err, {variant: 'error'})
  }

  const handleForgotPassword = () => {
    setOpenPasswordReset(true)
  }

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Box>
          <Stack spacing={3}>
            <Box>
              <Typography sx={{color: '#333', fontWeight: 'bold', fontSize: '0.9rem', mb: 1}}>Email address</Typography>
              <TextField
                fullWidth
                autoComplete="username"
                type="email"
                placeholder="Type your email address here"
                {...getFieldProps('email')}
                autoFocus
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                InputProps={{
                  sx: {
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      border: '1px solid #2FB7DE',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #2FB7DE',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    padding: '4px 12px',
                  },
                }}
              />
            </Box>

            <Box>
              <Typography sx={{color: '#333', fontWeight: 'bold', fontSize: '0.9rem', mb: 1}}>
                Enter your Password
              </Typography>
              <TextField
                fullWidth
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Type your password here"
                {...getFieldProps('password')}
                InputProps={{
                  sx: {
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      border: '1px solid #2FB7DE',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #2FB7DE',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                    padding: '4px 12px',
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowPassword} edge="end">
                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
              />
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{my: 3}}>
            <FormControlLabel
              control={
                <Checkbox
                  {...getFieldProps('remember')}
                  checked={values.remember}
                  sx={{
                    color: '#2FB7DE',
                    '&.Mui-checked': {
                      color: '#2FB7DE',
                    },
                  }}
                />
              }
              label={<Typography sx={{color: '#666', fontSize: '0.9rem'}}>Remember me?</Typography>}
            />

            <Link
              onClick={handleForgotPassword}
              variant="subtitle2"
              to="#"
              style={{
                color: '#2FB7DE',
                textDecoration: 'none',
                fontSize: '0.9rem',
                cursor: 'pointer',
              }}
            >
              Forgot Password?
            </Link>
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isLoading}
            loadingIndicator={<CircularProgress color="inherit" size={20} />}
            sx={{
              borderRadius: '8px',
              color: 'white',
              backgroundColor: '#2FB7DE',
              fontWeight: 'bold',
              fontSize: '1rem',
              padding: '12px 0',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#23A6DC',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
              },
              mb: 3,
            }}
          >
            Log in
          </LoadingButton>

          <Box sx={{textAlign: 'center', mt: 2}}>
            <Typography
              onClick={() => handleSwitchForm()}
              sx={{
                color: '#2FB7DE',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                textDecoration: 'none',
                mb: 2,
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Login using mobile number
            </Typography>

            <Typography
              sx={{
                color: '#666',
                fontSize: '0.9rem',
              }}
            >
              Don't have an account?{' '}
              <Typography
                component="span"
                onClick={handleOpenCreateAccount}
                sx={{
                  color: '#2FB7DE',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Create an account.
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Form>

      <ChangePass open={openPasswordReset} onClose={handleClosePassword} />

      <Dialog open={initialChoice} onClose={() => setInitialChoice(false)}>
        <DialogTitle>Choose User Type</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you an employee or a Client?</Typography>
          <Stack direction="row" spacing={2} sx={{mt: 2}}>
            <Button variant="contained" onClick={() => handleUserChoice('Employee')}>
              Employee
            </Button>
            <Button variant="outlined" onClick={() => handleUserChoice('Client Manager')}>
              Client Manager
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={openCreateAccount} onClose={handleCloseCreateAccount} maxWidth="xs" fullWidth>
        <DialogTitle sx={{textAlign: 'center', pb: 0}}>
          <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2}}>
            <img src="/favicon/logo.jpg" alt="Sparkle Timekeeping" style={{width: 100}} />
            <Typography variant="h6" fontWeight="bold" sx={{ml: 2}}>
              SPARKLE TIMEKEEPING
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{textAlign: 'center', px: 5, py: 3}}>
          <Box sx={{backgroundColor: '#f8f9fa', borderRadius: 2, p: 3, boxShadow: 1}}>
            {isStoreManager ? (
              <>
                <Typography variant="h6" fontWeight="bold" sx={{mb: 2}}>
                  Create Client Account
                </Typography>
                <Typography variant="body2" sx={{mb: 3}}>
                  Proceed to create a new client account.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => (window.location.href = '/store/create')}
                >
                  Create Client Account
                </Button>
              </>
            ) : (
              <>
                <Typography variant="h6" fontWeight="bold" sx={{mb: 2}}>
                  Create Employee Account
                </Typography>
                <Typography variant="body2" sx={{mb: 3}}>
                  Scan client QR code to proceed
                </Typography>
                <Box sx={{mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                  <img src={EmployeeVisualAid} alt="Visual Aid" style={{width: 150, marginBottom: '1rem'}} />
                  <br />
                  <br />
                  <img
                    src="https://media.istockphoto.com/id/1358621997/vector/qr-code-smartphone-scanner-linear-icon-vector-illustration.jpg?s=612x612&w=0&k=20&c=ePiWZHIbseW9GwmM498rRKC_Dvk8IsKv41nqnC8iZhQ="
                    alt="QR code"
                    style={{
                      width: 150,
                      marginBottom: '1rem',
                      borderRadius: 2,
                      boxShadow: 1,
                      border: '1px solid #f8f9fa',
                      padding: 2,
                      backgroundColor: '#fff',
                    }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => setOpenQR(true)}
                  sx={{
                    textTransform: 'none',
                    px: 5,
                    animation: 'pulse 1s infinite', // Add pulse animation
                    '@keyframes pulse': {
                      '0%': {transform: 'scale(1)'},
                      '50%': {transform: 'scale(1.05)'},
                      '100%': {transform: 'scale(1)'},
                    },
                  }}
                >
                  Scan Now
                </Button>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{justifyContent: 'center', flexDirection: 'column', py: 2}}>
          {/* <Typography variant="body2" sx={{ mb: 2 }}>
        Already have an account?{' '}
        <Button color="primary" sx={{ textTransform: 'none', p: 0 }} 
        onClick={handleCloseCreateAccount}>
          Sign in here
        </Button>
      </Typography> */}

          <Button onClick={handleCloseCreateAccount} variant="outlined" color="error" sx={{mt: 2}}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openQR} onClose={handleCloseQR}>
        <DialogTitle>
          Scan store QR code.
          <IconButton
            aria-label="close"
            onClick={handleCloseQR}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <>
            <QrReader
              delay={3000}
              onError={handleError}
              onScan={handleScan}
              // chooseDeviceId={()=>selected}
              style={{width: '250px', heigth: '200px'}}
              // className={'qrScanner'}
            />
            {/*<DialogTitle style={{ display: "flex", justifyContent: "center" }}><a href="https://www.sparkletimekeeping.com/store/create">Register store account</a></DialogTitle>*/}
          </>
        </DialogContent>
      </Dialog>
    </FormikProvider>
  )
}
